"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APP_PACKAGE_AUTOMATION_SLOT_VALUE_KEYS = exports.AppPackageAutomationFileSchema = exports.APP_PACKAGE_STEP_UNKNOWN = exports.APP_PACKAGE_AUTOMATION_FILE_MISSING = exports.APP_PACKAGE_AUTOMATION_MAX_BYTES = exports.APP_PACKAGE_AUTOMATION_MAX_COUNT = exports.APP_PACKAGE_AUTOMATION_FORMAT_VERSION = exports.APP_PACKAGE_AUTOMATION_PREFIX = void 0;
exports.isAppPackageAutomationKey = isAppPackageAutomationKey;
exports.appPackageAutomationPath = appPackageAutomationPath;
exports.readAppPackageAutomationKey = readAppPackageAutomationKey;
exports.isAppPackageButtonlessTrigger = isAppPackageButtonlessTrigger;
exports.serializeAppPackageAutomation = serializeAppPackageAutomation;
exports.namesAppPackageWorkspaceAccess = namesAppPackageWorkspaceAccess;
exports.listAppPackageAutomationWorkspaceSlots = listAppPackageAutomationWorkspaceSlots;
exports.checkAppPackageAutomation = checkAppPackageAutomation;
exports.appPackageAutomationStartIssues = appPackageAutomationStartIssues;
exports.formatAppPackageAutomationIssue = formatAppPackageAutomationIssue;
const automation_safety_contract_1 = require("@streamlain/automation-safety-contract");
const field_types_1 = require("@streamlain/field-types");
const workflow_field_schemas_1 = require("@streamlain/workflow-field-schemas");
const zod_1 = require("zod");
const app_base_contract_1 = require("./app-base-contract");
/**
 * An App package carries each of its automations as
 * `automations/<automationKey>.json`: those a button of the App starts and
 * those that run on new or changed rows of a package table or on a schedule. The file names tables by the source key
 * and columns by the column id of the package, never by an id of a workspace,
 * and it carries no access: whatever belongs to a workspace is chosen again
 * when the package is installed.
 */
exports.APP_PACKAGE_AUTOMATION_PREFIX = 'automations/';
exports.APP_PACKAGE_AUTOMATION_FORMAT_VERSION = 1;
exports.APP_PACKAGE_AUTOMATION_MAX_COUNT = 16;
exports.APP_PACKAGE_AUTOMATION_MAX_BYTES = 256 * 1024;
/** Error code: a button of the App starts an automation the package carries no file for. */
exports.APP_PACKAGE_AUTOMATION_FILE_MISSING = 'APP_PACKAGE_AUTOMATION_FILE_MISSING';
/** A package with a step its reader does not know, or knows differently. */
exports.APP_PACKAGE_STEP_UNKNOWN = 'APP_PACKAGE_STEP_UNKNOWN';
const AUTOMATION_KEY = /^[a-z0-9][a-z0-9-]{0,62}$/;
const AUTOMATION_PATH = /^automations\/([a-z0-9][a-z0-9-]{0,62})\.json$/;
function isAppPackageAutomationKey(key) {
    return AUTOMATION_KEY.test(key);
}
function appPackageAutomationPath(automationKey) {
    return `${exports.APP_PACKAGE_AUTOMATION_PREFIX}${automationKey}.json`;
}
/** The automation key a package path stands for, or null for any other path. */
function readAppPackageAutomationKey(path) {
    return AUTOMATION_PATH.exec(path)?.[1] ?? null;
}
const Id = zod_1.z.string().min(1);
const FilterLeafSchema = zod_1.z
    .object({
    type: zod_1.z.literal('filter'),
    left: zod_1.z.object({ type: zod_1.z.literal('ref'), name: Id }).strict(),
    function: Id.optional(),
    args: zod_1.z.array(zod_1.z
        .object({
        type: zod_1.z.literal('literal'),
        value: zod_1.z.unknown().optional(),
    })
        .strict()),
})
    .strict();
const FilterGroupSchema = zod_1.z.lazy(() => zod_1.z
    .object({
    type: zod_1.z.literal('group'),
    op: zod_1.z.enum(['and', 'or']),
    conditions: zod_1.z.array(zod_1.z.union([FilterLeafSchema, FilterGroupSchema])),
})
    .strict());
const TriggerSchema = zod_1.z.discriminatedUnion('type', [
    zod_1.z.object({ type: zod_1.z.literal('webhook') }).strict(),
    zod_1.z
        .object({
        type: zod_1.z.literal('scheduled'),
        cronPreset: Id,
        timezone: Id,
    })
        .strict(),
    zod_1.z
        .object({
        type: zod_1.z.literal('row_created'),
        /** Minutes whose new rows one run gets (rowTriggerCollectDelayMs). */
        collectMinutes: automation_safety_contract_1.CollectMinutesSchema.optional(),
    })
        .strict(),
    zod_1.z
        .object({
        type: zod_1.z.literal('row_changed'),
        /** Columns of the source table; an empty list watches every column. */
        watchedColumns: zod_1.z.array(Id),
        /** Minutes whose changed rows one run gets (rowTriggerCollectDelayMs). */
        collectMinutes: automation_safety_contract_1.CollectMinutesSchema.optional(),
    })
        .strict(),
]);
/** Triggers that start an automation without a button of the App. */
function isAppPackageButtonlessTrigger(trigger) {
    return trigger.type !== 'webhook';
}
const AutomationSchema = zod_1.z
    .object({
    executionKind: zod_1.z.literal('workflow'),
    trigger: TriggerSchema,
    sourceKey: Id.optional(),
    filterGroup: FilterGroupSchema,
    selectedColumns: zod_1.z.array(Id),
    inputMappings: zod_1.z.array(zod_1.z
        .object({
        schemaFieldId: Id,
        columnId: Id,
        inputTableId: Id.optional(),
        source: zod_1.z.literal(field_types_1.INPUT_MAPPING_OPTIONS_SOURCE).optional(),
    })
        .strict()),
    inputTables: zod_1.z.array(zod_1.z
        .object({
        id: Id,
        sourceKey: Id,
        role: zod_1.z.enum(['lead', 'secondary']),
        filterGroup: FilterGroupSchema.optional(),
        linkedVia: Id.optional(),
        cardinality: zod_1.z.enum(['one', 'many']).optional(),
        multiFieldName: Id.optional(),
    })
        .strict()),
    outputTables: zod_1.z.array(zod_1.z
        .object({
        sourceKey: Id,
        mode: zod_1.z
            .enum([
            'update_source',
            'append',
            'append_each',
            'update_by_match',
            'upsert',
        ])
            .optional(),
        matchConfigId: Id.optional(),
        targetAlias: Id.optional(),
        sourceArrayField: Id.optional(),
    })
        .strict()),
    outputMappings: zod_1.z.array(zod_1.z
        .object({
        sourceKey: Id,
        /** A mapping that writes a fixed value reads no field of the answer. */
        responseField: Id.optional(),
        targetColumnId: Id,
        matchConfigId: Id.optional(),
        fixedValue: Id.optional(),
    })
        .strict()
        .superRefine((mapping, context) => {
        if (mapping.responseField === undefined &&
            mapping.fixedValue === undefined) {
            context.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['responseField'],
                message: 'names a field of the answer or a fixed value',
            });
        }
    })),
    matchConfigs: zod_1.z.array(zod_1.z
        .object({
        id: Id,
        sourceKey: Id,
        multiMatchBehavior: zod_1.z.enum([
            'fail',
            'update_all',
            'update_first',
            'skip',
            'ask',
        ]),
        notFoundBehavior: zod_1.z.enum(['fail', 'create']).optional(),
        stealBehavior: zod_1.z.enum(['fail', 'silent_steal', 'ask']).optional(),
        keys: zod_1.z
            .array(zod_1.z
            .object({
            columnId: Id,
            sourceField: Id,
            matchMode: Id,
        })
            .strict())
            .min(1),
    })
        .strict()),
    statusTracking: zod_1.z
        .object({
        enabled: zod_1.z.boolean(),
        mode: zod_1.z.enum(['status_column', 'delete_row']),
        columnId: Id.nullable(),
        createColumn: zod_1.z.object({ name: Id }).strict().optional(),
        standardOptions: zod_1.z.array(Id),
        pendingValue: Id.optional(),
        successValue: Id,
        errorValue: Id.optional(),
    })
        .strict(),
    batchConfig: zod_1.z
        .object({
        batchSize: zod_1.z.number().int().min(1).max(100),
        executionMode: zod_1.z.enum(['row', 'batch']).optional(),
        sendAsArray: zod_1.z.boolean().optional(),
        retry: zod_1.z
            .object({
            enabled: zod_1.z.boolean(),
            maxAttempts: zod_1.z.number().int().min(1),
            initialDelayMs: zod_1.z.number().int().min(0),
            backoffMultiplier: zod_1.z.number().min(1),
            maxDelayMs: zod_1.z.number().int().min(0),
        })
            .strict(),
        onFailure: zod_1.z.enum(['continue', 'stop']),
    })
        .strict(),
    /** Order in which runs take the source rows; absent means oldest first. */
    inputOrder: zod_1.z
        .object({
        by: zod_1.z.literal('created'),
        direction: zod_1.z.enum(['asc', 'desc']),
    })
        .strict()
        .optional(),
})
    .strict();
/**
 * The workflow the automation runs. Steps and edges keep the shape the
 * workflow engine owns; this contract only reads the settings of a step that
 * belong to a workspace.
 */
const WorkflowSchema = zod_1.z
    .object({
    name: Id,
    steps: zod_1.z.array(zod_1.z.record(zod_1.z.string(), zod_1.z.unknown())).min(1),
    edges: zod_1.z.array(zod_1.z.record(zod_1.z.string(), zod_1.z.unknown())),
    inputSchema: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
    outputSchema: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
    schemaVersion: zod_1.z.union([zod_1.z.number(), zod_1.z.string()]).optional(),
})
    .strict();
exports.AppPackageAutomationFileSchema = zod_1.z
    .object({
    formatVersion: zod_1.z.literal(exports.APP_PACKAGE_AUTOMATION_FORMAT_VERSION),
    title: Id,
    automation: AutomationSchema,
    workflow: WorkflowSchema,
})
    .strict();
/** Same content, same bytes: the checksum of a package does not depend on field order. */
function serializeAppPackageAutomation(file) {
    return (0, app_base_contract_1.stableJsonStringify)(file);
}
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function isUnset(value) {
    return value === undefined || value === null || value === '';
}
/** Where a text points to an access: `{{credential.<x>`, however it goes on. */
const ACCESS_REFERENCE = /\{\{\s*credential\.([^.}\s]*)/g;
/** The keys of a slot that hold its value: the one set and the one it falls back to. */
exports.APP_PACKAGE_AUTOMATION_SLOT_VALUE_KEYS = [
    'value',
    'defaultValue',
];
/** The lists of a step whose slots hold values. */
const VALUE_SLOT_LISTS = ['configSlots', 'inputSlots'];
/**
 * Whether a value of a step points to an access of a workspace: a
 * `{{credential.<x>.<field>}}` anywhere inside it whose `<x>` is not an access
 * the platform provides itself. A package carries such a field empty. The
 * placeholder form `__<type>__` and a reference that cannot be read count as
 * an access of a workspace.
 */
function namesAppPackageWorkspaceAccess(value) {
    if (typeof value === 'string') {
        return [...value.matchAll(ACCESS_REFERENCE)].some(reference => !(0, workflow_field_schemas_1.isPlatformCredentialType)(reference[1] ?? ''));
    }
    if (Array.isArray(value))
        return value.some(namesAppPackageWorkspaceAccess);
    if (isRecord(value)) {
        return Object.entries(value).some(([key, entry]) => namesAppPackageWorkspaceAccess(key) ||
            namesAppPackageWorkspaceAccess(entry));
    }
    return false;
}
/**
 * The settings of a step that point to an access of a workspace, by the key
 * the step keeps them under: the entries of its config and the values of its
 * slots. What a slot says about itself (description, placeholder) documents
 * the reference form and is not read.
 */
function workspaceAccessFields(step) {
    const fields = new Set();
    if (isRecord(step['config'])) {
        for (const [key, value] of Object.entries(step['config'])) {
            if (namesAppPackageWorkspaceAccess(value))
                fields.add(key);
        }
    }
    for (const list of VALUE_SLOT_LISTS) {
        const slots = step[list];
        if (!Array.isArray(slots))
            continue;
        for (const slot of slots) {
            if (isRecord(slot) &&
                exports.APP_PACKAGE_AUTOMATION_SLOT_VALUE_KEYS.some(key => namesAppPackageWorkspaceAccess(slot[key]))) {
                fields.add(typeof slot['name'] === 'string' ? slot['name'] : list);
            }
        }
    }
    return [...fields];
}
function workspaceLocalSlots(steps) {
    const found = [];
    for (const step of steps) {
        if (!isRecord(step) || !Array.isArray(step['configSlots']))
            continue;
        const stepId = typeof step['id'] === 'string' ? step['id'] : '';
        const stepTitle = typeof step['name'] === 'string' ? step['name'] : stepId;
        const config = isRecord(step['config']) ? step['config'] : {};
        for (const slot of step['configSlots']) {
            if (!isRecord(slot) || typeof slot['widget'] !== 'string')
                continue;
            const optionsSource = slot['optionsSource'];
            if (!(0, workflow_field_schemas_1.isWorkspaceLocalSlot)({
                widget: slot['widget'],
                optionsSource: typeof optionsSource === 'string' ? optionsSource : undefined,
            })) {
                continue;
            }
            const slotKey = typeof slot['name'] === 'string' ? slot['name'] : '';
            found.push({
                stepId,
                stepTitle,
                slotKey,
                slot,
                configValue: slotKey === '' ? undefined : config[slotKey],
            });
        }
    }
    return found;
}
/** The settings of the workflow steps that are chosen in the workspace the package is installed into. */
function listAppPackageAutomationWorkspaceSlots(steps) {
    return workspaceLocalSlots(steps).map(({ stepId, stepTitle, slotKey, slot }) => ({
        stepId,
        stepTitle,
        slotKey,
        labelsI18n: isRecord(slot['labelsI18n'])
            ? slot['labelsI18n']
            : {},
        required: slot['required'] === true,
        widget: slot['widget'],
        ...(typeof slot['optionsSource'] === 'string'
            ? { optionsSource: slot['optionsSource'] }
            : {}),
    }));
}
/**
 * Checks one automation file of an App package. An empty list means the file
 * can be packed and installed; every issue names the automation, the workflow
 * step where it belongs to one, and the field.
 */
function checkAppPackageAutomation(input) {
    const issues = [];
    const issue = (field, message, step) => {
        issues.push({
            automation: input.key,
            ...(step === undefined ? {} : { step }),
            field,
            message,
        });
    };
    if (!isAppPackageAutomationKey(input.key)) {
        issue('key', 'must be lowercase letters, digits and hyphens');
        return issues;
    }
    let size;
    try {
        size = new TextEncoder().encode(serializeAppPackageAutomation(input.content)).length;
    }
    catch {
        issue('file', 'is not JSON content');
        return issues;
    }
    if (size > exports.APP_PACKAGE_AUTOMATION_MAX_BYTES) {
        issue('file', `exceeds ${exports.APP_PACKAGE_AUTOMATION_MAX_BYTES} bytes`);
        return issues;
    }
    const parsed = exports.AppPackageAutomationFileSchema.safeParse(input.content);
    if (!parsed.success) {
        for (const problem of parsed.error.issues) {
            issue(problem.path.join('.') || 'file', problem.message);
        }
        return issues;
    }
    const { automation, workflow } = parsed.data;
    const columnsOf = (sourceKey) => sourceKey === undefined ? undefined : input.tables[sourceKey]?.columns;
    const requireTable = (field, sourceKey) => {
        if (!columnsOf(sourceKey)) {
            issue(field, `table "${sourceKey}" is not part of the package`);
        }
    };
    const requireColumn = (field, sourceKey, columnId) => {
        if (sourceKey === undefined) {
            issue(field, 'names a column, but the automation reads no source table');
            return undefined;
        }
        const columns = columnsOf(sourceKey);
        // A missing table is reported where the table is named.
        if (!columns)
            return undefined;
        const column = columns.find(candidate => candidate.id === columnId);
        if (!column) {
            issue(field, `column "${columnId}" is not part of table "${sourceKey}" in the package`);
        }
        return column;
    };
    const requireOption = (field, column, value) => {
        if (column?.type !== 'select' && column?.type !== 'multi-select')
            return;
        const options = Array.isArray(column.data?.['options'])
            ? column.data['options']
            : [];
        const optionIds = new Set(options.flatMap(option => isRecord(option) && typeof option['id'] === 'string'
            ? [option['id']]
            : []));
        const values = Array.isArray(value) ? value : [value];
        if (values.some(item => typeof item !== 'string' || !optionIds.has(item))) {
            issue(field, `must be an option id of column "${column.id}"`);
        }
    };
    const checkFilter = (field, sourceKey, group) => {
        group.conditions.forEach((condition, index) => {
            const path = `${field}.conditions.${index}`;
            if (condition.type === 'group') {
                checkFilter(path, sourceKey, condition);
                return;
            }
            const column = requireColumn(`${path}.left.name`, sourceKey, condition.left.name);
            condition.args.forEach((arg, argIndex) => requireOption(`${path}.args.${argIndex}.value`, column, arg.value));
        });
    };
    const webhook = automation.trigger.type === 'webhook';
    if (automation.trigger.type === 'scheduled' &&
        automation.trigger.cronPreset === 'custom') {
        issue('automation.trigger.cronPreset', 'a free schedule pattern cannot be packed');
    }
    const rowTrigger = automation.trigger.type === 'row_created' ||
        automation.trigger.type === 'row_changed';
    if (rowTrigger && automation.sourceKey === undefined) {
        issue('automation.sourceKey', 'an automation started by rows names the table whose rows start it');
    }
    if (automation.sourceKey !== undefined) {
        if (webhook) {
            issue('automation.sourceKey', 'an automation a button starts reads no source table');
        }
        else {
            requireTable('automation.sourceKey', automation.sourceKey);
        }
    }
    const mainTable = webhook ? undefined : automation.sourceKey;
    checkFilter('automation.filterGroup', mainTable, automation.filterGroup);
    if (automation.trigger.type === 'row_changed') {
        // The status column changes with every run only while the automation
        // writes it, as trackedStatusColumnId of the automation safety contract.
        const { statusTracking } = automation;
        const trackedStatusColumnId = statusTracking.enabled && statusTracking.mode !== 'delete_row'
            ? statusTracking.columnId
            : null;
        automation.trigger.watchedColumns.forEach((columnId, index) => {
            const field = `automation.trigger.watchedColumns.${index}`;
            requireColumn(field, mainTable, columnId);
            if (columnId === trackedStatusColumnId) {
                issue(field, 'the status column of the automation cannot start it; it changes with every run');
            }
        });
    }
    automation.selectedColumns.forEach((columnId, index) => requireColumn(`automation.selectedColumns.${index}`, mainTable, columnId));
    const inputTables = new Map(automation.inputTables.map(table => [table.id, table]));
    automation.inputTables.forEach((table, index) => {
        const path = `automation.inputTables.${index}`;
        requireTable(`${path}.sourceKey`, table.sourceKey);
        if (table.linkedVia !== undefined) {
            requireColumn(`${path}.linkedVia`, mainTable, table.linkedVia);
        }
        if (table.filterGroup) {
            checkFilter(`${path}.filterGroup`, table.sourceKey, table.filterGroup);
        }
    });
    automation.inputMappings.forEach((mapping, index) => {
        const path = `automation.inputMappings.${index}`;
        if ((0, field_types_1.isOptionsInputMapping)(mapping)) {
            if (webhook || mapping.inputTableId !== undefined) {
                issue(`${path}.source`, 'options are read from a select column of the main table only');
                return;
            }
            const column = requireColumn(`${path}.columnId`, mainTable, mapping.columnId);
            if (column &&
                column.type !== 'select' &&
                column.type !== 'multi-select') {
                issue(`${path}.columnId`, `column "${mapping.columnId}" has no options`);
            }
            return;
        }
        if (webhook) {
            if (mapping.inputTableId !== field_types_1.WEBHOOK_PAYLOAD_TABLE_ID) {
                issue(`${path}.inputTableId`, `an automation a button starts reads its input from "${field_types_1.WEBHOOK_PAYLOAD_TABLE_ID}"`);
            }
            return;
        }
        if (mapping.inputTableId === undefined) {
            requireColumn(`${path}.columnId`, mainTable, mapping.columnId);
            return;
        }
        const table = inputTables.get(mapping.inputTableId);
        if (!table) {
            issue(`${path}.inputTableId`, `input table "${mapping.inputTableId}" is not part of the automation`);
            return;
        }
        if (mapping.columnId !== field_types_1.RELATION_ROWS_COLUMN_ID) {
            requireColumn(`${path}.columnId`, table.sourceKey, mapping.columnId);
        }
    });
    const matchConfigs = new Set(automation.matchConfigs.map(config => config.id));
    const requireMatchConfig = (field, id) => {
        if (id !== undefined && !matchConfigs.has(id)) {
            issue(field, `match rule "${id}" is not part of the automation`);
        }
    };
    automation.matchConfigs.forEach((config, index) => {
        const path = `automation.matchConfigs.${index}`;
        requireTable(`${path}.sourceKey`, config.sourceKey);
        config.keys.forEach((key, keyIndex) => requireColumn(`${path}.keys.${keyIndex}.columnId`, config.sourceKey, key.columnId));
    });
    automation.outputTables.forEach((table, index) => {
        const path = `automation.outputTables.${index}`;
        requireTable(`${path}.sourceKey`, table.sourceKey);
        requireMatchConfig(`${path}.matchConfigId`, table.matchConfigId);
        if (webhook && table.mode === 'update_source') {
            issue(`${path}.mode`, 'an automation a button starts has no source row to write into');
        }
    });
    automation.outputMappings.forEach((mapping, index) => {
        const path = `automation.outputMappings.${index}`;
        requireTable(`${path}.sourceKey`, mapping.sourceKey);
        requireMatchConfig(`${path}.matchConfigId`, mapping.matchConfigId);
        const column = requireColumn(`${path}.targetColumnId`, mapping.sourceKey, mapping.targetColumnId);
        if (mapping.fixedValue !== undefined) {
            requireOption(`${path}.fixedValue`, column, mapping.fixedValue);
        }
    });
    if (automation.statusTracking.columnId !== null) {
        requireColumn('automation.statusTracking.columnId', mainTable, automation.statusTracking.columnId);
    }
    // A step keeps a setting twice: as the value of its slot and in its config.
    for (const found of workspaceLocalSlots(workflow.steps)) {
        if (isUnset(found.slot['value']) && isUnset(found.configValue))
            continue;
        issue(found.slotKey || 'configSlots', 'belongs to a workspace and is chosen when the package is installed; the package carries it empty', found.stepTitle);
    }
    for (const step of workflow.steps) {
        if (step['credentials'] === undefined || step['credentials'] === null) {
            continue;
        }
        const stepId = typeof step['id'] === 'string' ? step['id'] : '';
        issue('credentials', 'is an access of a workspace; a package carries none and it is chosen after the package is installed', typeof step['name'] === 'string' ? step['name'] : stepId);
    }
    // The issue names the place; what the setting holds is never repeated.
    for (const step of workflow.steps) {
        const stepId = typeof step['id'] === 'string' ? step['id'] : '';
        for (const field of workspaceAccessFields(step)) {
            issues.push({
                automation: input.key,
                step: typeof step['name'] === 'string' ? step['name'] : stepId,
                stepId,
                field,
                message: 'points to an access of a workspace; a package carries the field empty and the access is chosen after the package is installed',
            });
        }
    }
    return issues;
}
/** One line a person can act on: automation, step, field. */
/**
 * A button starts its automation over the webhook, also one that keeps a
 * schedule; rows never start an automation a button starts. An automation no
 * button starts runs on its own trigger: new or changed rows of its source
 * table or a schedule. A file that does not parse is reported by the
 * automation check.
 */
function appPackageAutomationStartIssues(key, content, startedByButton) {
    const parsed = exports.AppPackageAutomationFileSchema.safeParse(content);
    if (!parsed.success)
        return [];
    const { trigger } = parsed.data.automation;
    if (startedByButton &&
        (trigger.type === 'row_created' || trigger.type === 'row_changed')) {
        return [
            `Automation "${key}": a button of the App Module starts it, so new or changed rows cannot start it as well`,
        ];
    }
    if (!startedByButton && !isAppPackageButtonlessTrigger(trigger)) {
        return [
            `Automation "${key}": no button of the App Module starts it and it has no row or schedule trigger`,
        ];
    }
    return [];
}
function formatAppPackageAutomationIssue(issue) {
    const place = [`Automation "${issue.automation}"`];
    if (issue.step !== undefined)
        place.push(`step "${issue.step}"`);
    place.push(`field "${issue.field}"`);
    return `${place.join(' / ')}: ${issue.message}`;
}
